import { useCallback, useEffect, useRef, useState } from "react";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import {
  authorizedUser,
  redirectToLogin,
  stripBearerPrefix,
} from "src/helpers/Auth";
import { nuclearn_api_url } from "src/App";

export type DatasetArchiveStreamStatus =
  | "idle"
  | "running"
  | "ready"
  | "failed"
  | "cancelled";

export interface UseDatasetArchiveStreamOptions {
  datasetId: number;
  correlationId: string | null;
}

export interface DatasetArchiveStreamState {
  status: DatasetArchiveStreamStatus;
  processed: number;
  total: number;
  percent: number;
  stage: string;
  bytesWritten: number | null;
  downloadUrl: string | null;
  filename: string | null;
  sizeBytes: number | null;
  error: string | null;
  cancel: () => void;
}

interface InternalState {
  status: DatasetArchiveStreamStatus;
  processed: number;
  total: number;
  stage: string;
  bytesWritten: number | null;
  downloadUrl: string | null;
  filename: string | null;
  sizeBytes: number | null;
  error: string | null;
}

const INITIAL_STATE: InternalState = {
  status: "idle",
  processed: 0,
  total: 0,
  stage: "pending",
  bytesWritten: null,
  downloadUrl: null,
  filename: null,
  sizeBytes: null,
  error: null,
};

interface ProgressEventPayload {
  processed: number;
  total: number;
  stage: string;
  bytes_written: number | null;
}

interface ReadyEventPayload {
  download_url: string;
  filename: string;
  size_bytes: number;
}

interface ErrorEventPayload {
  message: string;
}

const resolveBaseUrl = (): string => {
  if (!nuclearn_api_url) return window.location.origin;
  return nuclearn_api_url.startsWith("/")
    ? `${window.location.origin}${nuclearn_api_url}`
    : nuclearn_api_url;
};

const buildStreamUrl = (
  datasetId: number,
  correlationId: string,
  token: string,
): string => {
  const url = new URL(
    `${resolveBaseUrl()}/dataset-archive/${datasetId}/download-all/stream/${correlationId}`,
  );
  // The stored token may already be 'bearer <jwt>' — the SSE endpoint wraps
  // ?token= with another 'Bearer ', so we must strip any existing prefix here
  // or the server sees a double prefix and rejects it.
  const normalizedToken = stripBearerPrefix(token).trim();
  if (normalizedToken) {
    url.searchParams.set("token", normalizedToken);
  }
  return url.toString();
};

const computePercent = (processed: number, total: number): number => {
  if (total <= 0) return 0;
  return Math.min(100, Math.round((processed / total) * 100));
};

const applyProgress = (
  prev: InternalState,
  payload: ProgressEventPayload,
): InternalState => ({
  ...prev,
  status: "running",
  processed: payload.processed,
  total: payload.total,
  stage: payload.stage,
  bytesWritten: payload.bytes_written,
});

const applyReady = (
  prev: InternalState,
  payload: ReadyEventPayload,
): InternalState => ({
  ...prev,
  status: "ready",
  downloadUrl: payload.download_url,
  filename: payload.filename,
  sizeBytes: payload.size_bytes,
});

const applyError = (
  prev: InternalState,
  payload: ErrorEventPayload,
): InternalState => ({
  ...prev,
  status: "failed",
  error: payload.message,
});

const parseSsePayload = <T>(raw: string): T | null => {
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

const reduceServerEvent = (
  prev: InternalState,
  event: { event: string; data: string },
): InternalState => {
  if (event.event === "progress") {
    const payload = parseSsePayload<ProgressEventPayload>(event.data);
    return payload ? applyProgress(prev, payload) : prev;
  }
  if (event.event === "ready") {
    const payload = parseSsePayload<ReadyEventPayload>(event.data);
    return payload ? applyReady(prev, payload) : prev;
  }
  if (event.event === "error") {
    const payload = parseSsePayload<ErrorEventPayload>(event.data);
    return payload ? applyError(prev, payload) : prev;
  }
  return prev;
};

const markConnectionLost = (prev: InternalState): InternalState =>
  prev.status === "ready" || prev.status === "cancelled"
    ? prev
    : { ...prev, status: "failed", error: prev.error ?? "Connection lost" };

const openArchiveStream = (
  datasetId: number,
  correlationId: string,
  signal: AbortSignal,
  onEvent: (event: { event: string; data: string }) => void,
  onConnectionLost: (err: unknown) => never,
): Promise<void> => {
  const token = authorizedUser().Authorization ?? "";
  // EventSource cannot attach an Authorization header, so the SSE endpoint
  // auths off ?token= only. Keeping the header here would be dead weight.
  return fetchEventSource(buildStreamUrl(datasetId, correlationId, token), {
    method: "GET",
    signal,
    openWhenHidden: true,
    async onopen(response) {
      if (response.ok) return;
      if (response.status === 401 || response.status === 403) {
        redirectToLogin();
        throw new Error("Unauthorized");
      }
      throw new Error(`Archive stream failed to open: ${response.status}`);
    },
    onmessage: onEvent,
    onerror: onConnectionLost,
  });
};

export const useDatasetArchiveStream = (
  options: UseDatasetArchiveStreamOptions,
): DatasetArchiveStreamState => {
  const { datasetId, correlationId } = options;
  const [state, setState] = useState<InternalState>(INITIAL_STATE);
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleServerEvent = useCallback(
    (event: { event: string; data: string }) =>
      setState((prev) => reduceServerEvent(prev, event)),
    [],
  );

  const cancel = useCallback(() => {
    // Mark cancelled BEFORE aborting: fetchEventSource's abort path invokes
    // onerror synchronously, which calls markConnectionLost. If that runs
    // first, state becomes "failed" and this setState would preserve it.
    // Marking cancelled up front lets markConnectionLost's own guard
    // short-circuit the subsequent onerror transition.
    setState((prev) =>
      prev.status === "ready" || prev.status === "failed"
        ? prev
        : { ...prev, status: "cancelled" },
    );
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);

  useEffect(() => {
    if (!correlationId) {
      setState(INITIAL_STATE);
      return;
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setState({ ...INITIAL_STATE, status: "running" });

    openArchiveStream(
      datasetId,
      correlationId,
      controller.signal,
      handleServerEvent,
      (err) => {
        // fetchEventSource invokes onerror on every abort (cancel, dep
        // change, unmount). Only treat it as a connection loss when the
        // abort didn't come from us.
        if (!controller.signal.aborted) {
          setState(markConnectionLost);
        }
        throw err;
      },
    ).catch(() => {
      // Errors already surfaced via onConnectionLost; swallow to avoid unhandled rejection.
    });

    return () => controller.abort();
  }, [datasetId, correlationId, handleServerEvent]);

  const percent =
    state.status === "ready"
      ? 100
      : computePercent(state.processed, state.total);

  return {
    status: state.status,
    processed: state.processed,
    total: state.total,
    percent,
    stage: state.stage,
    bytesWritten: state.bytesWritten,
    downloadUrl: state.downloadUrl,
    filename: state.filename,
    sizeBytes: state.sizeBytes,
    error: state.error,
    cancel,
  };
};
